/**
 * useShareholderState - Hook for managing shareholder structure state
 *
 * Extracted from CompanyDetails.jsx to reduce file size and improve maintainability.
 * Manages all state related to the Shareholder Structure tab and popup:
 * - Filter state and validation
 * - Popup toggle state
 * - The desktop Graph or Table view of the tab
 * - The prepared card graph (prepareShareholderGraph), the ultimate shareholder list, the node
 *   figures of the graph view, its request status and the '+N owners' collapse set
 * - The Lock layout switch (whether cards can be dragged), shared by the tab graph and the popup
 *
 * Hover, selection and dragged card positions of the card graph live in the graph component,
 * not here.
 *
 * @module useShareholderState
 */

import { useLocalObservable } from 'mobx-react';
import { observable, toJS } from 'mobx';
import {
  COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS,
  getErrorFields,
  trimStateFields
} from '../../../../utils/constants/fields';
import { mapFieldsToState } from '../../../../utils/utils';
import {
  prepareShareholderGraph,
  toggleCollapsedNodeId
} from '../ShareholderStructure/graph/prepareShareholderGraph';
import { STRUCTURE_VIEWS } from '../ShareholderStructure/graph/graphConstants';
import { hasOwnLocation } from '../ShareholderStructure/graph/graphCardModel';

/** Request status of the shareholder graph view (GET_PROFILER_SHAREHOLDER_GRAPH). */
export const SHAREHOLDER_GRAPH_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed'
});

const GRAPH_STATUS_VALUES = Object.values(SHAREHOLDER_GRAPH_STATUS);

const SHARE_PERCENT_FIELD = COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.SHAREHOLDER_SHARE_PERCENT.NAME;
const UNKNOWN_VALUE_FIELD = COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.ENTITIES_UNKNOWN_VALUE.NAME;
const SUBSIDIARIES_FIELD = COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.INCLUDE_SUBSIDIARIES.NAME;
const GENERAL_PARTNERS_FIELD =
  COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.EXPAND_GENERAL_PARTNERS.NAME;

const DEFAULT_SHARE_PERCENT = '5';
const SHOW_ALL_SHARE_PERCENT = '0';

const getDefaultShareholderFilters = () => ({
  ...mapFieldsToState(COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS),
  [SHARE_PERCENT_FIELD]: DEFAULT_SHARE_PERCENT,
  [UNKNOWN_VALUE_FIELD]: true
});

const FILTER_FIELD_NAMES = Object.values(COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS).map(
  (field) => field.NAME
);
const DEFAULT_FILTER_VALUES = Object.freeze(getDefaultShareholderFilters());

/** True when two filter forms differ in any field. */
const filtersDiffer = (a, b) => FILTER_FIELD_NAMES.some((name) => a[name] !== b[name]);

/** Applied filter form values -> the filters prepareShareholderGraph expects. */
const toGraphFilters = (appliedFilters) => ({
  percentageFilter: Number.parseFloat(appliedFilters[SHARE_PERCENT_FIELD]),
  includeUnknownShares: appliedFilters[UNKNOWN_VALUE_FIELD],
  includeSubsidiaries: appliedFilters[SUBSIDIARIES_FIELD],
  expandGeneralPartners: !!appliedFilters[GENERAL_PARTNERS_FIELD]
});

/** Neutral value of each applied filter: removing its chip sets it. */
const NEUTRAL_FILTER_VALUES = Object.freeze({
  percentageFilter: { field: SHARE_PERCENT_FIELD, value: SHOW_ALL_SHARE_PERCENT },
  expandGeneralPartners: { field: GENERAL_PARTNERS_FIELD, value: true },
  includeUnknownShares: { field: UNKNOWN_VALUE_FIELD, value: true },
  includeSubsidiaries: { field: SUBSIDIARIES_FIELD, value: true }
});

const hasShareholderStructure = (structure) =>
  !!structure?.nodes?.length && !!structure?.links?.length;

// The fallback location a graph's target card was built with.
const locationKeyOf = (location) => `${location?.city ?? ''}|${location?.country ?? ''}`;

/**
 * Hook for managing shareholder structure state
 *
 * @param {Object} deps - Dependencies
 * @param {Function} deps.getShareholdersStructure - Getter for shareholders structure data
 * @param {Function} [deps.getTargetLocation] - Getter for { city, country } of the target company
 * @param {Object} deps.utilsStore - Utils store with lockScroll
 * @returns {Object} Observable state object
 */
const useShareholderState = ({ getShareholdersStructure, getTargetLocation, utilsStore }) => {
  const state = useLocalObservable(
    () => ({
      // ============================================
      // Filter State
      // ============================================
      popupShareholderStructureAppliedFilters: getDefaultShareholderFilters(),
      popupShareholderStructureFilters: getDefaultShareholderFilters(),
      setPopupShareholderStructureFiltersValue: (field, value) => {
        state.popupShareholderStructureFilters[field?.NAME] = value;
      },
      onSubmitErrorStatePopupShareholderStructure: false,
      setOnSubmitErrorStatePopupShareholderStructure: (value = false) =>
        (state.onSubmitErrorStatePopupShareholderStructure = value),

      // Filter validation
      get validationFieldsPopupShareholderStructure() {
        return getErrorFields(
          Object.values(COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS),
          state.popupShareholderStructureFilters
        );
      },

      // Filter disabled state
      get isPopupShareholderStructureFilterDisabled() {
        const hasDiff = filtersDiffer(
          state.popupShareholderStructureFilters,
          state.popupShareholderStructureAppliedFilters
        );
        return (
          state.validationFieldsPopupShareholderStructure.invalidFields.filter((f) => !f.isOnSubmit)
            .length ||
          (state.setOnSubmitErrorStatePopupShareholderStructure &&
            state.validationFieldsPopupShareholderStructure.invalidFields.length) ||
          !hasDiff
        );
      },

      // Clear filter disabled state (all filters at their default values). Show owners of general
      // partners counts as the user set it: a value that only followed the cards clears nothing.
      get isClearFiltersDisabled() {
        return !filtersDiffer(
          {
            ...state.popupShareholderStructureAppliedFilters,
            [GENERAL_PARTNERS_FIELD]: state.generalPartnersChoice
          },
          DEFAULT_FILTER_VALUES
        );
      },

      /** The applied filters in the shape prepareShareholderGraph expects. */
      get appliedGraphFilters() {
        return toGraphFilters(state.popupShareholderStructureAppliedFilters);
      },

      // ============================================
      // Filter Actions
      // ============================================
      /**
       * 'Show owners of general partners' as the user last applied it (Filter, its chip, Clear
       * filter), also when Filter keeps a value that followed the cards. A value that only followed
       * the cards goes back to it for the next company.
       */
      generalPartnersChoice: !!DEFAULT_FILTER_VALUES[GENERAL_PARTNERS_FIELD],

      /**
       * The only writer of the applied filters. A write by the user records 'Show owners of general
       * partners' as the choice; a changed value also drops the manual collapse set, so the new
       * default applies, while other changes keep the manual choices. keepCollapseSet: the change
       * follows the cards or undoes that for a new company, so neither happens. keepChoice: the
       * chip of another filter was removed, which says nothing about this checkbox.
       */
      setAppliedShareholderFilters: (
        next,
        { keepCollapseSet = false, keepChoice = false } = {}
      ) => {
        const generalPartners = !!next[GENERAL_PARTNERS_FIELD];
        if (!keepCollapseSet) {
          if (
            generalPartners !==
            !!state.popupShareholderStructureAppliedFilters[GENERAL_PARTNERS_FIELD]
          ) {
            state.collapsedNodeIds = null;
          }
          if (!keepChoice) {
            state.generalPartnersChoice = generalPartners;
          }
        }
        state.popupShareholderStructureAppliedFilters = next;
      },

      /**
       * Sets 'Show owners of general partners' in the applied filters, keeping the collapse set.
       * The pending filters (both panels show them) follow unless the checkbox has an unsaved edit
       * there; other pending edits stay.
       */
      setGeneralPartnersFilter: (value) => {
        const applied = !!state.popupShareholderStructureAppliedFilters[GENERAL_PARTNERS_FIELD];
        if (value === applied) {
          return;
        }
        state.setAppliedShareholderFilters(
          { ...state.popupShareholderStructureAppliedFilters, [GENERAL_PARTNERS_FIELD]: value },
          { keepCollapseSet: true }
        );
        if (!!state.popupShareholderStructureFilters[GENERAL_PARTNERS_FIELD] === applied) {
          state.popupShareholderStructureFilters = {
            ...state.popupShareholderStructureFilters,
            [GENERAL_PARTNERS_FIELD]: value
          };
        }
      },

      /**
       * While the cards are opened and closed by hand (a collapse set), every rebuild unchecks
       * 'Show owners of general partners' when a general partner card on screen hides an owner
       * (collapsedGeneralPartnerCount), the rule of its chip, so the chip shows at once. A rebuild
       * without a click (Filter, a chip, new figures) checks it again only where that chip just
       * went away because an owner of a general partner is back. Under the filter's own default it
       * is the user's choice and stays as it is.
       *
       * @param {Object|null} previous - the graph before this rebuild
       */
      syncGeneralPartnersFilter: (previous) => {
        if (state.collapsedNodeIds == null || !state.graph) {
          return;
        }
        if (state.graph.collapsedGeneralPartnerCount > 0) {
          state.setGeneralPartnersFilter(false);
        } else if (previous?.collapsedGeneralPartnerCount > 0) {
          state.checkGeneralPartnerOwnersBack(previous);
        }
      },

      /**
       * Checks 'Show owners of general partners' when no general partner card on screen hides an
       * owner and an owner of a general partner is on screen that the previous graph did not show,
       * also through another stake while the general partner stays collapsed. Called after every
       * card click; without a click only where the chip just went away (syncGeneralPartnersFilter).
       *
       * @param {Object|null} previous - the graph before the click or rebuild
       */
      checkGeneralPartnerOwnersBack: (previous) => {
        const { graph } = state;
        if (!graph || !previous || graph.collapsedGeneralPartnerCount > 0) {
          return;
        }
        const shownBefore = new Set(previous.generalPartnerOwnerIds);
        if (graph.generalPartnerOwnerIds.some((id) => !shownBefore.has(id))) {
          state.setGeneralPartnersFilter(true);
        }
      },

      onPopupShareholderStructureFilter: () => {
        trimStateFields(state.popupShareholderStructureFilters);
        if (state.validationFieldsPopupShareholderStructure.invalidFields.length) {
          if (!state.onSubmitErrorStatePopupShareholderStructure) {
            state.setOnSubmitErrorStatePopupShareholderStructure(true);
          }
          return;
        }

        state.setAppliedShareholderFilters(
          structuredClone(toJS(state.popupShareholderStructureFilters))
        );

        if (!state.toggledShareholderPopup) {
          state.toggleMiniShareholderStructureFilters();
        }

        state.regenerateGraph();
      },

      /** Back to the default filters and the default collapse, manual collapses included. */
      onPopupShareholderStructureClearFilter: () => {
        state.setAppliedShareholderFilters(getDefaultShareholderFilters());
        state.popupShareholderStructureFilters = getDefaultShareholderFilters();
        state.collapsedNodeIds = null;

        if (!state.toggledShareholderPopup) {
          state.toggleMiniShareholderStructureFilters();
        }

        state.regenerateGraph();
      },

      /**
       * 'Show all' on the aggregate: lifts the two filters that hide its owners, the share filter
       * (0 %) and 'Unknown values: excluded', in the applied and the pending filters, leaving
       * subsidiaries and the owners of general partners alone. Never opens the mini filter panel;
       * closes it only when it is open.
       */
      showAllFiltered: () => {
        state.removeAppliedFilters(['percentageFilter', 'includeUnknownShares']);
      },

      /**
       * A chip's X: puts those filters back to neutral in the applied and pending filters and
       * leaves the others alone. Like Show all, never opens the mini panel and closes it when open.
       *
       * @param {string[]} [filterKeys] - appliedGraphFilters keys; unknown keys are ignored
       */
      removeAppliedFilters: (filterKeys = []) => {
        const keys = filterKeys.filter((key) => Object.hasOwn(NEUTRAL_FILTER_VALUES, key));
        if (!keys.length) {
          return;
        }

        if (state.toggledMiniShareholderStructureFilters) {
          state.toggleMiniShareholderStructureFilters();
        }

        const neutral = Object.fromEntries(
          keys.map((key) => [NEUTRAL_FILTER_VALUES[key].field, NEUTRAL_FILTER_VALUES[key].value])
        );
        state.setAppliedShareholderFilters(
          { ...state.popupShareholderStructureAppliedFilters, ...neutral },
          { keepChoice: !keys.includes('expandGeneralPartners') }
        );
        state.popupShareholderStructureFilters = {
          ...state.popupShareholderStructureFilters,
          ...neutral
        };
        state.onSubmitErrorStatePopupShareholderStructure = false;

        state.regenerateGraph();
      },

      // ============================================
      // Popup/Filter Toggle State
      // ============================================
      toggledMiniShareholderStructureFilters: false,
      toggleMiniShareholderStructureFilters: () => {
        state.toggledMiniShareholderStructureFilters =
          !state.toggledMiniShareholderStructureFilters;
        state.popupShareholderStructureFilters = structuredClone(
          toJS(state.popupShareholderStructureAppliedFilters)
        );
      },

      toggledShareholderPopup: false,
      /**
       * Opens or closes the full-screen popup. An open mini filter panel is closed first (which
       * resets its pending values to the applied ones), because both views share the pending
       * filter form.
       */
      toggleShareholderPopup: () => {
        if (state.toggledMiniShareholderStructureFilters) {
          state.toggleMiniShareholderStructureFilters();
        }

        state.toggledShareholderPopup = !state.toggledShareholderPopup;
        utilsStore.lockScroll(state.toggledShareholderPopup);
      },

      // ============================================
      // Structure View State
      // ============================================
      /**
       * Desktop Graph or Table view of the Shareholders tab. Kept for the page session (the tab
       * unmounts on every tab switch); not reset per company, like the filters.
       */
      structureView: STRUCTURE_VIEWS.GRAPH,
      setStructureView: (view) => {
        if (!Object.values(STRUCTURE_VIEWS).includes(view)) {
          return;
        }
        state.structureView = view;
      },

      // ============================================
      // Card Graph State
      // ============================================
      /** PreparedGraph from prepareShareholderGraph, or null (no structure). */
      graph: null,
      /** The fallback location (locationKeyOf) the graph's target card was built with. */
      graphTargetLocationKey: null,
      /** Ultimate shareholders of the filtered graph (General tab and table). */
      ultimateShareholders: [],
      /** NodeFigures by String(member_id) from the graph view, or null before it resolved. */
      graphFigures: null,
      graphStatus: SHAREHOLDER_GRAPH_STATUS.IDLE,
      /**
       * Collapsed node ids (Set), or null for the default of the applied filters: owners of
       * general-partner-only nodes collapsed unless Show owners of general partners is applied.
       */
      collapsedNodeIds: null,
      /**
       * Cards cannot be dragged while true (Lock layout). Shared by the tab graph and the
       * full-screen popup; every company starts locked.
       */
      isLayoutLocked: true,
      toggleLayoutLock: () => {
        state.isLayoutLocked = !state.isLayoutLocked;
      },
      /**
       * True when the structure has no card figures: it came from the details payload (graph view
       * failed, older BE) or an older BE answered the graph view with the legacy flow.
       */
      isGraphFiguresUnavailable: false,

      /** Figure state the cards show: '…' while loading, n/a when failed or unavailable. */
      get figuresStatus() {
        if (state.graphStatus === SHAREHOLDER_GRAPH_STATUS.LOADED) {
          return state.isGraphFiguresUnavailable
            ? SHAREHOLDER_GRAPH_STATUS.FAILED
            : SHAREHOLDER_GRAPH_STATUS.LOADED;
        }
        if (state.graphStatus === SHAREHOLDER_GRAPH_STATUS.FAILED) {
          return SHAREHOLDER_GRAPH_STATUS.FAILED;
        }
        return SHAREHOLDER_GRAPH_STATUS.LOADING;
      },

      setGraphFiguresUnavailable: (value = false) => {
        state.isGraphFiguresUnavailable = !!value;
      },

      setGraphFigures: (figuresByMemberId) => {
        state.graphFigures = figuresByMemberId || null;
        state.regenerateGraph();
      },

      setGraphStatus: (status) => {
        if (!GRAPH_STATUS_VALUES.includes(status)) {
          console.error('ERROR - setGraphStatus - Unknown graph status:', status);
          return;
        }
        state.graphStatus = status;
      },

      /** Collapses or expands the owners of one node, starting from what the graph shows now. */
      toggleNodeCollapsed: (nodeId) => {
        if (nodeId == null) {
          return;
        }
        const previous = state.graph;
        state.collapsedNodeIds = toggleCollapsedNodeId(previous, nodeId);
        state.regenerateGraph();
        state.checkGeneralPartnerOwnersBack(previous);
      },

      /**
       * 'Show all owners' in the table view: expands every collapsed card (the default GP rule and
       * manual collapses), so Show owners of general partners follows as checked when a general
       * partner was hiding an owner. Unchecking it later goes back to the default rule.
       */
      expandAllOwners: () => {
        const previous = state.graph;
        state.collapsedNodeIds = new Set();
        state.regenerateGraph();
        state.checkGeneralPartnerOwnersBack(previous);
      },

      /**
       * Clears everything that belongs to one company. Overlay mode reuses one CompanyDetails
       * instance across companies, so without this the previous company's graph, figures,
       * collapse state or unlocked layout would show on the next one. An open full-screen popup
       * is closed too (it has no loading or error state of its own) and the scroll lock released.
       * Show owners of general partners goes back to the user's choice: a value that only followed
       * the previous company's cards does not carry over.
       */
      resetForCompany: () => {
        if (state.toggledShareholderPopup) {
          state.toggleShareholderPopup();
        }

        state.graph = null;
        state.ultimateShareholders = [];
        state.graphFigures = null;
        state.graphStatus = SHAREHOLDER_GRAPH_STATUS.IDLE;
        state.collapsedNodeIds = null;
        state.setGeneralPartnersFilter(state.generalPartnersChoice);
        state.isGraphFiguresUnavailable = false;
        state.isLayoutLocked = true;

        if (state.toggledMiniShareholderStructureFilters) {
          state.toggleMiniShareholderStructureFilters();
        }
      },

      clearGraph: () => {
        state.graph = null;
        state.ultimateShareholders = [];
      },

      /**
       * Rebuilds the card graph for the current structure, applied filters, figures and collapse
       * state, then lets Show owners of general partners follow the chip (syncGeneralPartnersFilter).
       * An empty structure clears the graph and the ultimate shareholders (no stale graph of a
       * previous company).
       */
      regenerateGraph: () => {
        const shareholdersStructure = getShareholdersStructure();

        if (!hasShareholderStructure(shareholdersStructure)) {
          state.clearGraph();
          return;
        }

        try {
          const targetLocation = getTargetLocation?.() || undefined;
          const prepared = prepareShareholderGraph(
            shareholdersStructure,
            state.appliedGraphFilters,
            {
              figuresByMemberId: state.graphFigures,
              collapsedNodeIds: state.collapsedNodeIds,
              targetLocation
            }
          );
          const previous = state.graph;
          state.graph = prepared;
          state.graphTargetLocationKey = locationKeyOf(targetLocation);
          state.ultimateShareholders = prepared?.ultimateShareholders ?? [];
          state.syncGeneralPartnersFilter(previous);
        } catch (error) {
          console.error(
            'ERROR - regenerateGraph - Could not prepare the shareholder graph:',
            error
          );
          state.clearGraph();
        }
      },

      /**
       * New company details: rebuilds the graph only when its target card shows the fallback
       * location (the target node names no place of its own) and that location changed. A
       * rebuild clears the hover highlight and resets the graph's error boundary, so it must not
       * happen for every details response.
       */
      refreshTargetLocation: () => {
        const target = state.graph?.nodes.find((node) => node.id === state.graph.targetId)?.raw;
        if (!target || hasOwnLocation(target)) {
          return;
        }
        if (locationKeyOf(getTargetLocation?.()) !== state.graphTargetLocationKey) {
          state.regenerateGraph();
        }
      }
    }),
    {
      graph: observable.ref,
      ultimateShareholders: observable.ref,
      graphFigures: observable.ref,
      collapsedNodeIds: observable.ref
    }
  );

  return state;
};

export default useShareholderState;
